/**
 * i18n Configuration
 *
 * The panel deals with three distinct sets of languages. Keeping them apart is
 * what stops admin UI strings and customer-facing content from leaking into
 * each other:
 *
 * 1. Admin UI languages — ADMIN_LANGUAGE_CODES below. Locales carrying a
 *    complete translation of the panel itself. These and only these may be
 *    resolved by i18n.t(), so supportedLngs is derived from the list rather
 *    than repeated alongside it.
 *
 * 2. Content-only locales — CONTENT_ONLY_LANGUAGE_CODES below. Registered as
 *    resources so pricing-rule duration labels can be generated for them
 *    through getResource(), but deliberately kept out of supportedLngs: their
 *    UI translation is absent or incomplete, and a browser set to one of them
 *    must not flip the panel into a half-translated state that the language
 *    switcher does not offer and therefore cannot undo.
 *
 * 3. Merchant content languages — LANGUAGE_CONFIGS in LanguageContext. What a
 *    merchant may offer its customers, currently 27 codes. Most have no locale
 *    file at all, which is fine: stored translations are shown as they are, and
 *    PricingRulesEditor only auto-generates a label where locale data exists.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from '../locales/en.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import es from '../locales/es.json';
import it from '../locales/it.json';
import hu from '../locales/hu.json';
import fi from '../locales/fi.json';
// Content-only locale: supplies pricing-rule duration labels for merchants who
// offer Danish to customers. Deliberately absent from supportedLngs below, so
// it can never become the admin UI language — see src/locales/da.json.
import da from '../locales/da.json';

/**
 * Locales with a complete admin UI translation. Drives both i18next's
 * supportedLngs and the language switcher (LanguageContext derives
 * availableAdminLanguages from this list).
 *
 * A locale joins this list only once src/locales/<code>.json covers the full
 * key set of en.json — an incomplete file here renders a mixed-language panel.
 */
export const ADMIN_LANGUAGE_CODES = ['en', 'de', 'fr', 'es', 'it', 'fi'];

/**
 * Locales registered for content generation only, never for the admin UI:
 *   hu — roughly half the UI translated; move it to ADMIN_LANGUAGE_CODES once
 *        src/locales/hu.json is complete.
 *   da — pricing-rule duration keys only, never intended as a UI language.
 */
export const CONTENT_ONLY_LANGUAGE_CODES = ['hu', 'da'];

// Initialize i18next with language detection
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      es: { translation: es },
      it: { translation: it },
      hu: { translation: hu },
      fi: { translation: fi },
      da: { translation: da },
    },
    fallbackLng: 'en',
    // Derived, never hand-listed: a locale that is resolvable by t() is by
    // definition one the whole panel can be displayed in.
    supportedLngs: ADMIN_LANGUAGE_CODES,
    debug: false,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
  });

// Listen for custom language change events from LanguageContext
window.addEventListener('languageChanged', ((event: CustomEvent) => {
  const { language } = event.detail;
  if (i18n.language !== language) {
    i18n.changeLanguage(language);
  }
}) as EventListener);

export default i18n;
